import { Box, BoxProps, Button, Typography } from "@mui/material";
import { alpha, styled, Theme } from "@mui/material/styles";

type ContainerProps = {
  isAI: boolean;
  messageIndex: number;
};

export const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI" && prop !== "messageIndex"
})(({ isAI, messageIndex }: ContainerProps) => ({
  display: "flex",
  flexDirection: isAI ? "row" : "row-reverse",
  alignItems: "flex-start",
  width: "100%",
  marginTop: messageIndex === 0 ? "auto" : 0
}));

type AvatarContainerProps = {
  theme: Theme;
  isAI: boolean;
};

export const AvatarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI"
})(({ theme, isAI }: AvatarContainerProps) => ({
  borderRadius: "4px",
  flexShrink: 0,
  backgroundColor: isAI ? "transparent" : theme.customColors.backgroundSecondaryAlternative,
  border: isAI ? "none" : `1px solid ${theme.customColors.borderPrimaryCta}`,
  width: "37px",
  height: "37px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
}));

type MessageContainerProps = {
  theme: Theme;
  isAI: boolean;
};

export const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI"
})(({ theme, isAI }: MessageContainerProps) => ({
  backgroundColor: isAI ? theme.customColors.backgroundPrimary : theme.customColors.backgroundSecondary,
  border: `1px solid ${theme.customColors.border}`,
  borderRadius: "8px",
  display: "flex",
  padding:"0.75rem"
}));

type MessageBoxProps = {
  theme: Theme;
  isAI: boolean;
};

export const MessageBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI"
})(({ theme, isAI }: MessageBoxProps) => ({
  color: isAI ? theme.customColors.textPrimary : theme.customColors.textInput,
  fontSize: "1rem",
  animation: "blink 0.25s steps(2, start) infinite",
  "@keyframes blink": {
    "50%": { borderRightColor: "transparent" }
  },
  "> p": {
    margin: 0
  }
}));

export const SuggestedQuestionsRow = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  flexWrap: "wrap",
  gap: "8px",
  width: "100%",
  marginTop: "10px",
  paddingLeft: "47px"
}));

type SuggestedQuestionChipProps = {
  chipIndex: number;
};

export const SuggestedQuestionChip = styled(Button, {
  shouldForwardProp: (prop) => prop !== "chipIndex"
})(({ theme, chipIndex }: { theme: Theme } & SuggestedQuestionChipProps) => ({
  textTransform: "none",
  textAlign: "left",
  justifyContent: "flex-start",
  fontSize: "13px",
  lineHeight: 1.3,
  color: theme.customColors.textPrimary,
  backgroundColor: theme.customColors.backgroundPrimary,
  border: `1px solid ${theme.customColors.textPrimary}`,
  borderRadius: "10px",
  padding: "8px 14px",
  maxWidth: "80%",
  opacity: 0,
  animation: "suggestedChipFadeIn 0.35s ease-out forwards",
  animationDelay: `${chipIndex * 0.1}s`,
  "@keyframes suggestedChipFadeIn": {
    from: {
      opacity: 0,
      transform: "translateY(8px)"
    },
    to: {
      opacity: 1,
      transform: "translateY(0)"
    }
  },
  "&:hover": {
    backgroundColor: alpha(theme.customColors.textPrimary, 0.08),
    borderColor: theme.customColors.textPrimary
  },
  "&.Mui-disabled": {
    borderColor: alpha(theme.customColors.textPrimary, 0.5),
    color: theme.customColors.textPrimary
  }
}));

export const MessageMeta = styled(Box)<BoxProps & { isAI?: boolean }>(({ isAI }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isAI ? "start" : "end",
  flexWrap: "wrap",
  rowGap: "4px",
  columnGap: "10px",
  marginTop: "4px"
}));

export const Timestamp = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "0.75rem"
}));

export const CheckMark = styled(Box)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  display: "inline-flex",
  alignItems: "center",
  fontSize: "0.875rem"
}));

export const RateExperienceRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  rowGap: "2px",
  columnGap: "10px",
  padding: 0
}));

export const RateExperienceLink = styled(Button)(({ theme }) => ({
  color: theme.customColors.accentVariantA,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "12px",
  padding: 0,
  minWidth: "auto",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline"
  }
}));
